import { changeFrameAsync, updateTranscriptBulk, fetchTranscriptAsync, switchPlay, updateTranscript, updateTranscriptActivateIdx } from 'actions/annotation-actions';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import {connect} from 'react-redux';
import { CombinedState, ObjectType } from 'reducers';
import Layout from 'antd/lib/layout';
import Alert from 'antd/lib/alert';
import { frameToRelativeSeconds, getTimelineFrameRate, secondsToFrame } from 'utils/media-time';


import TranscriptUtteranceText from './transcript-utterance-text';

interface StateToProps {
    transcriptData: any,
    transcriptFetching: boolean;
    startFrame: number;
    stopFrame: number;
    canvasIsReady: boolean;
    playing: boolean;
    frameNumber: number;
    dataFrameRate: number | null;
    phase: string;
    allStates: any[];
    allStatesFrameImages: any[];
    audioData: any
    activatedIdx: number;
}

interface DispatchToProps {
    fetchTranscript: () => void;
    changeFrame: (frame: number) => void;
    onSwitchPlay: (play: boolean) => void;
    onChangeTranscript(index: number, segment: any): void;
    onChangeTranscriptBulk(indexes: number[], segments: any[]): void;
    setActivatedIndex(index: number): void;
}


function mapStateToProps(state: CombinedState): StateToProps {
    const {
        annotation: {
            job: {
                instance: {
                    startFrame,
                    stopFrame,
                    phase,
                    dataFrameRate,
                }
            },
            annotations: {
                allStates,
                allStatesFrameImages
            },
            player: {
                transcript: {
                    data: transcriptData,
                    fetching: transcriptFetching,
                    activatedIdx
                },
                playing,
                frame: {
                    number: frameNumber,
                },
                audio: {
                    data: audioData,
                }
            },
            canvas: {
                ready: canvasIsReady
            }
        },
    } = state;

    return {
        transcriptData,
        transcriptFetching,
        canvasIsReady,
        playing,
        frameNumber,
        startFrame,
        stopFrame,
        dataFrameRate,
        phase,
        allStates: phase === 'phase0' ? [] : allStates,
        allStatesFrameImages: phase === 'phase0' ? [] : allStatesFrameImages,
        audioData,
        activatedIdx,
    }
}

function mapDispatchToProps(dispatch: any): DispatchToProps {
    return {
        fetchTranscript: (): void => dispatch(fetchTranscriptAsync()),
        changeFrame(frame: number): void {
            dispatch(changeFrameAsync(frame));
        },
        onSwitchPlay(playing: boolean): void {
            dispatch(switchPlay(playing));
        },
        onChangeTranscript(index: number, segment: any): void {
            dispatch(updateTranscript(index, segment));
        },
        onChangeTranscriptBulk(indexes: number[], segments: any[]) {
            dispatch(updateTranscriptBulk(indexes, segments));
        },
        setActivatedIndex(index: number) {
            dispatch(updateTranscriptActivateIdx(index))
        }
    }
}

const currentStyle = {
    fontWeight: 'bold',
    opacity: '100%'
}

const otherStyle = {
    opacity: '50%'
}

function hasTranscriptContent(transcriptData: any): boolean {
    if (!transcriptData) {
        return false;
    }

    const transcriptFull = String(transcriptData.transcript_full || '').trim();
    const wordSegments = transcriptData.word_segments || [];
    const segments = transcriptData.segments || [];
    const hasSegmentText = segments.some((segment: any) =>
        String(segment?.text || '').trim() && !segment?.attention_check
    );

    return Boolean(transcriptFull || wordSegments.length || hasSegmentText);
}

function getEmptyTranscriptNotice(transcriptData: any): string {
    return transcriptData?.meta?.asr_empty_result_message ||
        'The transcript is currently empty. For some videos, the ASR model may return no text. '
        + 'This is not a bug, and annotators can add transcript text manually.';
}



function TranscriptPlayerComponent(props: StateToProps & DispatchToProps) {
    const {
        transcriptData,
        transcriptFetching,
        playing, frameNumber,
        dataFrameRate, startFrame,
        stopFrame, phase, allStates,
        allStatesFrameImages,
        audioData,
        activatedIdx,
        fetchTranscript, onSwitchPlay, changeFrame, onChangeTranscript, setActivatedIndex
    } = props;
    const frameRate = getTimelineFrameRate(dataFrameRate);

    const [croppedImageMap, setCroppedImageMap] = useState<any>(null);
    const frameToTrascriptSegmentMap = useRef<number[]>([]);
    const scrollFn = useCallback((node: HTMLDivElement) => {
        if (node) {
         node.scrollIntoView({
             behavior: 'smooth',
             block: 'center',
             inline: 'center'
         })
        }
       }, []);
    useEffect(() => {
        // cropped images of the people
        const peopleIndices :number[] = [];
        allStates.forEach((state, idx) => {
            // person tags which are NOT audio selection
            if (state.label.name.startsWith('person:') && state.objectType !== ObjectType.AUDIOSELECTION && state.serverID != null) {
                peopleIndices.push(idx);
            }
        })
        const images = peopleIndices.map(() => new Image());
        Promise.all(images.map(async (image, i) => {
            const cnv = document.createElement('canvas');
            const ctx = cnv.getContext('2d');

            const p1X = Math.min(allStates[peopleIndices[i]].points[0], allStates[peopleIndices[i]].points[2]);
            const p2X = Math.max(allStates[peopleIndices[i]].points[0], allStates[peopleIndices[i]].points[2]);

            const p1Y = Math.min(allStates[peopleIndices[i]].points[1], allStates[peopleIndices[i]].points[3]);
            const p2Y = Math.max(allStates[peopleIndices[i]].points[1], allStates[peopleIndices[i]].points[3]);

            const sX = p1X,
                sY = p1Y,
                sW = p2X - p1X,
                sH = p2Y - p1Y;

            cnv.width = sW;
            cnv.height = sH;
            return await (new Promise((resolve) => {
                image.onload = () => {
                    ctx?.drawImage(image, sX, sY, sW, sH, 0, 0, sW, sH);
                    const cropped = cnv.toDataURL();
                    cnv.remove();
                    resolve(cropped);
                };
                image.src = URL.createObjectURL(allStatesFrameImages[peopleIndices[i]]);
            }));
        })).then((imgs) => setCroppedImageMap(
            imgs.reduce((acc: any, cur, idx) => {
                acc[allStates[peopleIndices[idx]].serverID] = cur;
                return acc;
            }, {})
            )
        );

    }, [allStates, allStatesFrameImages]);

    const [audioBlobURL, setAudioBlobURL] = useState('');
    const [audioFrameMap, setAudioFrameMap] = useState<any>(null);
    useEffect(() => {
        if(audioData) {
            const blb = new Blob([audioData], { type: 'audio/mp4' });
            setAudioBlobURL(URL.createObjectURL(blb));
        }

        return () => {
            if (audioBlobURL.length) {
                URL.revokeObjectURL(audioBlobURL)
            }
        }
    }, [audioData]);

    useEffect(() => {
        setAudioFrameMap(
            allStates.
                filter((state) =>
                    state.label.name.startsWith('person:') &&
                    state.objectType === ObjectType.AUDIOSELECTION && state.serverID != null
                ).
                reduce((acc: any, cur) => {
                    acc[cur.serverID] = cur.frame;
                    return acc;
                }, {})
        );
    }, [allStates])

    const [currentIdx, setCurrentIdx] = useState(-1);
    const [speakerCount, setSpeakerCount] = useState(0);
    const [selected, setSelected] = useState<number[]>([]);
    const [clickedIdx, setClickedIdx] = useState(-1);

    useEffect(() => {
        let maxSpeakers = 0;
        if (transcriptData?.segments?.length) {

            frameToTrascriptSegmentMap.current = new Array(stopFrame - startFrame + 1).fill(-1);
            transcriptData.segments.forEach((segment: any, index: number) => {
                if (segment.speaker)
                    maxSpeakers = Math.max(maxSpeakers, parseInt(segment.speaker.split('_')[1]));
                const sgmtStartFrame = secondsToFrame(segment.start, startFrame, stopFrame, frameRate);
                const sgmtEndFrame = secondsToFrame(segment.end, startFrame, stopFrame, frameRate);
                for (let i = sgmtStartFrame; i<=sgmtEndFrame; i++) {
                    frameToTrascriptSegmentMap.current[i - startFrame] = index;
                }
            })
        } else {
            frameToTrascriptSegmentMap.current = [];
        }
        setSpeakerCount(maxSpeakers + 1);
    }, [frameRate, startFrame, stopFrame, transcriptData]);



    const clearSelected = useCallback(() => {
        setSelected([]);
    }, []);

    const changeSpeakerBulk = useCallback((speaker: string) => {
        const sel = Array.from(new Set<number>(selected));
        const segments: any[] = [];
        for( const idx of sel) {
            segments.push({
                ...transcriptData.segments[idx],
                speaker: speaker
            })
        }

        updateTranscriptBulk(sel, segments);
    }, [selected, transcriptData])


    // stuff for multiselect
    useEffect(() => {
        if (phase === 'phase0') {
            const keyDownFn = (event: KeyboardEvent) => {
                if (event.shiftKey && event.key === 'ArrowUp') {
                    if (!selected.length) {
                        selected.push(currentIdx);
                        selected.push(currentIdx - 1);
                    }
                    else if (currentIdx < selected[selected.length - 1]) {
                        selected.pop();
                    }
                    else {
                        if (selected[selected.length - 1] > 0) {
                            selected.push(selected[selected.length - 1] - 1);
                        }
                    }
                } else if (event.shiftKey && event.key === 'ArrowDown') {
                    if (!selected.length) {
                        selected.push(currentIdx);
                        selected.push(currentIdx + 1);
                    }
                    else if (currentIdx > selected[selected.length - 1]) {
                        selected.pop();
                    }
                    else {
                        if (selected[selected.length - 1] < transcriptData?.segments?.length) {
                            selected.push(selected[selected.length - 1] + 1);
                        }
                    }

                }
                setSelected([...selected]);
            }


            document.addEventListener('keydown', keyDownFn);

            return () => {
                document.removeEventListener('keydown', keyDownFn);
            }
        }
    }, [selected, currentIdx, transcriptData]);

    useEffect(() => {
        if (!transcriptData && !transcriptFetching) {
            fetchTranscript();
        }

    }, []);

    useEffect(() => {
        setSelected([]);
    }, [transcriptData]);
    useEffect(() => {
        setClickedIdx(-1);
    }, [transcriptData]);

    const audioRef = useRef<HTMLAudioElement>(null);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const playAudio = useCallback((play: boolean, frame: number) => {
        if (audioRef.current ) {
            if (play) {
                audioRef.current.currentTime = frameToRelativeSeconds(frame, startFrame, frameRate);
                audioRef.current.play();
                timeoutRef.current = setTimeout(() => {
                    audioRef.current && audioRef.current.pause();
                }, 5000)
            } else {
                audioRef.current.pause();
                timeoutRef.current && clearTimeout(timeoutRef.current)
            }
        }
    }, [audioBlobURL, frameRate, startFrame]);
    const mappedIdx = frameToTrascriptSegmentMap.current[frameNumber - startFrame] != null ?
        frameToTrascriptSegmentMap.current[frameNumber - startFrame] : -1;
    let currentIdxNew = mappedIdx;
    if (
        clickedIdx !== -1 &&
        transcriptData?.segments?.[clickedIdx]
    ) {
        const clickedStartFrame = secondsToFrame(
            transcriptData.segments[clickedIdx].start,
            startFrame,
            stopFrame,
            frameRate,
        );
        if (frameNumber === clickedStartFrame) {
            currentIdxNew = clickedIdx;
        }
    }
    if (currentIdx !== currentIdxNew) {
        setCurrentIdx(currentIdxNew);
    }
    const transcriptHasContent = hasTranscriptContent(transcriptData);
    const showEmptyTranscriptNotice = Boolean(transcriptData && !transcriptHasContent);

    return <>
        <audio ref={audioRef} src={audioBlobURL}></audio>
        <div style={{padding: '10px', textAlign: 'center', color: 'white'}}>
            {!transcriptData && !transcriptFetching && 'No Transcript'}
            {!transcriptData && transcriptFetching && 'Loading Transcript...'}
            {transcriptData && <>
                {showEmptyTranscriptNotice && (
                    <Alert
                        type='info'
                        showIcon
                        message='Transcript is empty'
                        description={getEmptyTranscriptNotice(transcriptData)}
                        style={{ marginBottom: 12, textAlign: 'left' }}
                    />
                )}
                <TranscriptUtterancesMemo
                    transcriptData={transcriptData}
                    speakerCount={speakerCount}
                    frameRate={frameRate}
                    audioFrameMap={audioFrameMap}
                    changeFrame={changeFrame}
                    changeSpeakerBulk={changeSpeakerBulk}
                    clearSelected={clearSelected}
                    croppedImageMap={croppedImageMap}
                    currentIdx={currentIdx}
                    onChangeTranscript={onChangeTranscript}
                    onSwitchPlay={onSwitchPlay}
                    phase={phase}
                    playAudio={playAudio}
                    playing={playing}
                    selected={selected}
                    stopFrame={stopFrame}
                    scrollFn={scrollFn}

                    editingIdx={activatedIdx}
                    setEditingIdx={setActivatedIndex}
                    onSelectSegment={setClickedIdx}
                    startFrame={startFrame}

                />
            </>}
        </div>
       
    </>



}

export default connect(mapStateToProps, mapDispatchToProps)(TranscriptPlayerComponent);

interface TranscriptUtterancesProps {
    transcriptData: any;
    currentIdx: number;
    phase: string;
    speakerCount: number;
    stopFrame: number;
    playing: boolean;
    frameRate: number;
    selected: any;
    croppedImageMap: any;
    audioFrameMap: any;
    playAudio: (play: boolean, frame: number) => void;
    onChangeTranscript: (index: number, segment: any) => void;
    clearSelected: () => void;
    changeSpeakerBulk: (speaker: string) => void;
    changeFrame: (frameNumber: number) => void;
    onSwitchPlay: (play: boolean) => void;
    scrollFn: (target: HTMLDivElement) => void;

    editingIdx: number;
    setEditingIdx: (idx: number) => void;
    onSelectSegment: (idx: number) => void;
    startFrame: number;
}
function TranscriptUtterances (props: TranscriptUtterancesProps) : JSX.Element {
    const {
        transcriptData, currentIdx, phase, speakerCount, stopFrame,
        playing,
        changeFrame, onSwitchPlay, editingIdx, setEditingIdx,
        onChangeTranscript, frameRate, selected, clearSelected, changeSpeakerBulk,
        croppedImageMap, audioFrameMap, playAudio, scrollFn, onSelectSegment, startFrame
    } = props;
    return (<>
    {transcriptData.segments.map((segment: any, index: number) =>
        {
            const isCurrent = currentIdx === index;
            const appliedStyle = (isCurrent) ? currentStyle : otherStyle;

            return <TranscriptUtteranceText
                phase={phase}
                key={segment.key ?? `${segment.start}-${segment.end}-${index}`}
                speakerCount={speakerCount}
                appliedStyle={appliedStyle} isCurrent={isCurrent} stopFrame={stopFrame}
                changeFrame={changeFrame} playing={playing}
                segment={segment} index={index} frameRate={frameRate}
                onSwitchPlay={onSwitchPlay} onChangeTranscript={onChangeTranscript}
                isSelected={selected.includes(index)}
                scrollFn={scrollFn}
                isLastSelected={selected && selected[selected.length - 1] === index}
                clearSelected={clearSelected}
                changeSpeakerBulk={changeSpeakerBulk}
                images = {phase === 'phase0' ? null : croppedImageMap}
                audios = {phase === 'phase0' ? null : audioFrameMap}
                playAudio={playAudio}
                editingIdx={editingIdx}
                setEditingIdx={setEditingIdx}
                onSelectSegment={onSelectSegment}
                startFrame={startFrame}
            />
        }
        )}</>)
}


const TranscriptUtterancesMemo = React.memo(TranscriptUtterances);
